import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'your-supabase-url'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your-supabase-anon-key'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database helper functions
export const auth = {
  signUp: async (email, password, userData) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })
    return { data, error }
  },

  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  getUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser()
    return { user, error }
  },

  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback)
  }
}

export const database = {
  // User profile functions
  createUserProfile: async (userId, profileData) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .insert({
        id: userId,  // id column must match user_id for the reference constraint
        user_id: userId,
        ...profileData
      })
      .select()
      .single()
    return { data, error }
  },

  getUserProfile: async (userId) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single()
    return { data, error }
  },

  updateUserProfile: async (userId, updates) => {
    // First try to update existing profile
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('user_id', userId)
      .select()
      .single()

    // If no rows were updated (profile doesn't exist), create it
    if (error && error.code === 'PGRST116') {
      return await supabase
        .from('user_profiles')
        .insert({
          id: userId,  // id column must match user_id for the reference constraint
          user_id: userId,
          ...updates
        })
        .select()
        .single()
    }

    return { data, error }
  },

  // Address functions
  getUserAddresses: async (userId) => {
    const { data, error } = await supabase
      .from('user_addresses')
      .select('*')
      .eq('user_id', userId)
      .order('is_default', { ascending: false })
    return { data, error }
  },

  addUserAddress: async (addressData) => {
    const { data, error } = await supabase
      .from('user_addresses')
      .insert(addressData)
      .select()
      .single()
    return { data, error }
  },

  updateUserAddress: async (addressId, updates) => {
    const { data, error } = await supabase
      .from('user_addresses')
      .update(updates)
      .eq('id', addressId)
      .select()
      .single()
    return { data, error }
  },

  deleteUserAddress: async (addressId) => {
    const { error } = await supabase
      .from('user_addresses')
      .delete()
      .eq('id', addressId)
    return { error }
  },

  // Order functions
  createOrder: async (orderData) => {
    const { data, error } = await supabase
      .from('orders')
      .insert(orderData)
      .select()
      .single()
    return { data, error }
  },

  getUserOrders: async (userId) => {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          *,
          products (
              name,
              images
          )
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  getOrderById: async (orderId) => {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          *,
          products (
              name,
              images
          )
        )
      `)
      .eq('id', orderId)
      .single()
    return { data, error }
  },

  // Product functions
  getProducts: async (filters = {}) => {
    let query = supabase
      .from('products')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })

    if (filters.category) {
      query = query.eq('category', filters.category)
    }

    if (filters.minPrice) {
      query = query.gte('price', filters.minPrice)
    }

    if (filters.maxPrice) {
      query = query.lte('price', filters.maxPrice)
    }

    if (filters.search) {
      query = query.ilike('name', `%${filters.search}%`)
    }

    const { data, error } = await query
    return { data, error }
  },

  // Test database connection
  testConnection: async () => {
    try {
      const { data, error } = await supabase.from('products').select('count').limit(1);
      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  },

  getProductById: async (productId) => {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', productId)
      .single()
    return { data, error }
  },

  // Wishlist functions
  getUserWishlist: async (userId) => {
    const { data, error } = await supabase
      .from('wishlist')
      .select(`
        *,
        products (
          id,
          name,
          price,
          image,
          category
        )
      `)
      .eq('user_id', userId)
    return { data, error }
  },

  addToWishlist: async (userId, productId) => {
    const { data, error } = await supabase
      .from('wishlist')
      .insert({ user_id: userId, product_id: productId })
      .select()
      .single()
    return { data, error }
  },

  removeFromWishlist: async (userId, productId) => {
    const { error } = await supabase
      .from('wishlist')
      .delete()
      .eq('user_id', userId)
      .eq('product_id', productId)
    return { error }
  },

  // Cart functions
  getUserCart: async (userId) => {
    // Get cart items without JOIN first
    const { data: cartItems, error: cartError } = await supabase
      .from('cart_items')
      .select('*')
      .eq('user_id', userId)

    if (cartError || !cartItems) {
      return { data: null, error: cartError }
    }

    // Get product details for each cart item
    const cartWithProducts = await Promise.all(
      cartItems.map(async (cartItem) => {
        try {
          const { data: product, error: productError } = await supabase
            .from('products')
            .select('id, name, price, images, category')
            .eq('id', cartItem.product_id)
            .eq('is_active', true)
            .single()

          if (productError) {
            return {
              ...cartItem,
              products: null
            }
          }

          return {
            ...cartItem,
            products: product
          }
        } catch (error) {
          return {
            ...cartItem,
            products: null
          }
        }
      })
    )

    return { data: cartWithProducts, error: null }
  },

  addToCart: async (userId, productId, quantity = 1) => {
    // First check if item already exists in cart
    const { data: existingItem, error: checkError } = await supabase
      .from('cart_items')
      .select('*')
      .eq('user_id', userId)
      .eq('product_id', productId)
      .single()

    if (existingItem && !checkError) {
      // Update quantity
      const { data, error } = await supabase
        .from('cart_items')
        .update({ quantity: existingItem.quantity + quantity })
        .eq('id', existingItem.id)
        .select()
        .single()
      return { data, error }
    } else {
      // Add new item
      const { data, error } = await supabase
        .from('cart_items')
        .insert({
          user_id: userId,
          product_id: productId,
          quantity: quantity
        })
        .select()
        .single()
      return { data, error }
    }
  },

  updateCartItem: async (cartItemId, quantity) => {
    if (quantity <= 0) {
      // Remove item if quantity is 0 or less
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', cartItemId)
      return { error }
    }

    const { data, error } = await supabase
      .from('cart_items')
      .update({ quantity })
      .eq('id', cartItemId)
      .select()
      .single()
    return { data, error }
  },

  removeFromCart: async (cartItemId) => {
    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('id', cartItemId)
    return { error }
  },

  clearUserCart: async (userId) => {
    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('user_id', userId)
    return { error }
  },

  // Migrate localStorage cart to database
  migrateCartToDatabase: async (userId, localCartItems) => {
    const results = [];
    for (const item of localCartItems) {
      try {
        const result = await database.addToCart(userId, item.id, item.quantity);
        results.push(result);
      } catch (error) {
        console.error('Error migrating cart item:', item, error);
      }
    }

    return results;
  },

  // Admin functions
  getUserRole: async (userId) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('user_id', userId)
      .single();

    return { data, error };
  },

  setUserRole: async (userId, role) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .upsert({
        id: userId,
        user_id: userId,
        role: role
      })
      .select()
      .single();

    return { data, error };
  },

  // Admin product management
  createProduct: async (productData) => {
    const { data, error } = await supabase
      .from('products')
      .insert(productData)
      .select()
      .single();

    return { data, error };
  },

  updateProduct: async (productId, updates) => {
    const { data, error } = await supabase
      .from('products')
      .update(updates)
      .eq('id', productId)
      .select()
      .single();

    return { data, error };
  },

  deleteProduct: async (productId) => {
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', productId);

    return { error };
  },

  getAllProducts: async () => {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('created_at', { ascending: false });

    return { data, error };
  },

  // Admin user management
  getAllUsers: async () => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false });

    return { data, error };
  },

  // Posts functions (for homepage events/banners/content)
  getPosts: async (includeInactive = false) => {
    let query = supabase
      .from('posts')
      .select('*')
      .order('position', { ascending: true, nullsLast: true })
      .order('created_at', { ascending: false });

    if (!includeInactive) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;
    return { data, error };
  },

  createPost: async (postData) => {
    const { data, error } = await supabase
      .from('posts')
      .insert(postData)
      .select()
      .single();

    return { data, error };
  },

  updatePost: async (postId, updates) => {
    const { data, error } = await supabase
      .from('posts')
      .update(updates)
      .eq('id', postId)
      .select()
      .single();

    return { data, error };
  },

  deletePost: async (postId) => {
    const { error } = await supabase
      .from('posts')
      .delete()
      .eq('id', postId);

    return { error };
  },

  // Storage functions for image uploads
  uploadImage: async (file, folder = 'product-images') => {
    try {
      // Generate a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      
      // Upload file to Supabase Storage
      const { data, error } = await supabase
        .storage
        .from('product-images')
        .upload(`${folder}/${fileName}`, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw error;
      }

      // Get the public URL
      const { data: publicData } = supabase
        .storage
        .from('product-images')
        .getPublicUrl(`${folder}/${fileName}`);

      return {
        data: {
          path: data.path,
          publicUrl: publicData.publicUrl
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  deleteImage: async (filePath) => {
    try {
      const { data, error } = await supabase
        .storage
        .from('product-images')
        .remove([filePath]);

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  listImages: async (folder = 'product-images') => {
    try {
      const { data, error } = await supabase
        .storage
        .from('product-images')
        .list(folder, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' }
        });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
}

export default supabase
